/**
 * Home-feed card: nearest gas stations.
 *
 * Pulls the public CKAN dataset of fuel stations (already configured at
 * `Ckan.resources.gasStations`) and shows the 3 closest to the user's
 * current GPS location. Each item links to Google Maps for navigation.
 *
 * Renders nothing if GPS permission isn't granted — silent failure is
 * better than a permission popup the user didn't ask for.
 */

import { Ckan } from '../../app/config.js';
import * as ckan from '../../data/apis/ckan.js';
import { createLogger } from '../../core/logger.js';
import { escape as esc } from '../../shared/escape.js';

const log = createLogger('fuel-card');

/** localStorage key — cache the user's last known GPS for 10 minutes so
 *  we don't request a new fix every time the home feed re-renders. */
const GPS_CACHE_KEY = 'c2c_fuel_lastGps';
const GPS_TTL_MS = 10 * 60 * 1000;

/**
 * Render the card into the given element. Idempotent — call again to
 * refresh. Returns the element so callers can append it to the feed.
 */
/**
 * Render the fuel mini-card. The host element already has the D4 skeleton
 * (set by homeView.renderFuelMini) — we update its inner contents in place.
 * If the host doesn't have D4 markup (legacy callers), we fall back to
 * a self-contained list rendering.
 */
export async function renderInto(host) {
  if (!host) return;
  const isD4 = host.classList.contains('b-mini-card');

  try {
    const gps = await getGps();
    if (!gps) {
      writeStatus(host, isD4, 'אין הרשאת מיקום', 'אפשר שיתוף מיקום לראות תחנות');
      return;
    }
    const stations = await fetchStations(gps);
    if (stations.length === 0) {
      writeStatus(host, isD4, 'אין תוצאות', 'לא נמצאו תחנות בקרבה');
      return;
    }
    if (isD4) {
      // Show the cheapest / nearest in the value slot
      const top = stations[0];
      host.innerHTML = `
        <div class="b-top-row"><div class="b-badge">⛽</div><span class="b-chev">›</span></div>
        <div>
          <div class="b-title">דלק זול</div>
          <div class="b-value">${esc(top.name)}</div>
          <div class="b-sub">${esc(formatDist(top.distKm))} · ${stations.length} תחנות בקרבה</div>
        </div>
      `;
      // Click the entire card → opens Google Maps to the nearest station
      host.style.cursor = 'pointer';
      host.classList.add('b-card-clickable');
      host.onclick = () => {
        window.open(
          `https://www.google.com/maps?q=${encodeURIComponent(top.lat + ',' + top.lng)}`,
          '_blank',
        );
      };
      return;
    }
    // Legacy fallback (used by stand-alone info-card mounts)
    host.classList.add('info-card');
    host.innerHTML = `
      <div class="info-card-header">⛽ דלק זול בקרבת מקום</div>
      <div class="fuel-list">${stations.map((s) => `
        <div class="fuel-item">
          <span class="fuel-item-name">${esc(s.name)}</span>
          <span class="fuel-item-dist">${esc(formatDist(s.distKm))}</span>
          <a class="fuel-item-link"
             href="https://www.google.com/maps?q=${encodeURIComponent(s.lat + ',' + s.lng)}"
             target="_blank" rel="noopener">ניווט ↗</a>
        </div>`).join('')}</div>
    `;
  } catch (err) {
    log.warn('fuel card failed:', err?.message);
    writeStatus(host, isD4, 'שגיאה', 'לא ניתן לטעון תחנות כעת');
  }
}

/** Render a status / empty message into the host, in either D4 or legacy style. */
function writeStatus(host, isD4, title, sub) {
  if (isD4) {
    host.innerHTML = `
      <div class="b-top-row"><div class="b-badge">⛽</div></div>
      <div>
        <div class="b-title">דלק זול</div>
        <div class="b-sub">${esc(title)} · ${esc(sub)}</div>
      </div>
    `;
    return;
  }
  host.classList.add('info-card');
  host.innerHTML = `
    <div class="info-card-header">⛽ ${esc(title)}</div>
    <div class="info-card-empty">${esc(sub)}</div>
  `;
}

// ====================================================================

async function getGps() {
  // Cache lookup
  try {
    const raw = localStorage.getItem(GPS_CACHE_KEY);
    if (raw) {
      const c = JSON.parse(raw);
      if (c && c.at && Date.now() - c.at < GPS_TTL_MS && c.lat && c.lng) {
        return { lat: c.lat, lng: c.lng };
      }
    }
  } catch (_) { /* ignore */ }

  if (typeof navigator === 'undefined' || !navigator.geolocation) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const out = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        try { localStorage.setItem(GPS_CACHE_KEY, JSON.stringify({ ...out, at: Date.now() })); }
        catch (_) { /* ignore */ }
        resolve(out);
      },
      () => resolve(null),
      { maximumAge: GPS_TTL_MS, timeout: 6000, enableHighAccuracy: false },
    );
  });
}

/** Fetch all stations once, return the closest 3 by haversine distance.
 *  Uses `ckan.search()` instead of direct fetch — the shared helper
 *  routes through the `ckanProxy` Cloud Function so CORS doesn't block
 *  the browser. Also dedups in-flight requests across rerenders.
 */
async function fetchStations({ lat, lng }) {
  /** @type {any[]} */
  const records = await ckan.search({
    resourceId: Ckan.resources.gasStations,
    limit: 2000,
  });

  /** @type {Array<{ name: string, lat: number, lng: number, distKm: number }>} */
  const withDist = [];
  for (const r of records) {
    const slat = Number(r['נ.צ. רוחב'] ?? r.lat);
    const slng = Number(r['נ.צ. אורך'] ?? r.lng);
    if (!Number.isFinite(slat) || !Number.isFinite(slng)) continue;
    const name = String(r['שם_תחנה'] ?? r['חברה'] ?? 'תחנת דלק').trim();
    const distKm = haversineKm(lat, lng, slat, slng);
    if (distKm > 30) continue; // cap at 30km radius
    withDist.push({ name, lat: slat, lng: slng, distKm });
  }
  withDist.sort((a, b) => a.distKm - b.distKm);
  return withDist.slice(0, 3);
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
function toRad(d) { return (d * Math.PI) / 180; }

function formatDist(km) {
  if (km < 1) return `${Math.round(km * 1000)} מ׳`;
  return `${km.toFixed(1)} ק"מ`;
}
